import { useState, useEffect, useCallback } from 'react';
import {
  Drawer, Box, Typography, Chip, IconButton, Tabs, Tab,
  Accordion, AccordionSummary, AccordionDetails,
  Select, MenuItem, FormControl, TextField, Button,
  Dialog, DialogTitle, DialogContent,
  CircularProgress, Divider, Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { getAssetChecks, updateCheck, uploadCkl, getStigLibrary, applyStig } from '../api/stigMgmt';

const SEV_COLOR  = { high: '#f44336', medium: '#ff9800', low: '#ffeb3b' };
const SEV_LABEL  = { high: 'CAT I',  medium: 'CAT II',  low: 'CAT III' };
const STAT_COLOR = {
  pass:           '#4caf50',
  fail:           '#f44336',
  not_reviewed:   '#7a8fa6',
  not_applicable: '#00bcd4',
  accepted_risk:  '#ff9800',
};

function CheckRow({ check, onUpdate }) {
  const [open, setOpen]       = useState(false);
  const [saving, setSaving]   = useState(false);
  const [status, setStatus]   = useState(check.status);
  const [comment, setComment] = useState(check.comment || '');

  const effectiveColor = STAT_COLOR[status] || '#7a8fa6';

  const save = async () => {
    setSaving(true);
    const isOverride = ['not_applicable', 'accepted_risk'].includes(status);
    await updateCheck(check.id, {
      status:          isOverride ? check.raw_status : status,
      override_status: isOverride ? status : '',
      comment,
      reviewed_by: 'user',
    });
    setSaving(false);
    onUpdate();
  };

  return (
    <Accordion
      expanded={open}
      onChange={() => setOpen(o => !o)}
      sx={{ backgroundColor: '#0d1825', border: '1px solid #1e3a5f', borderRadius: '4px !important', mb: 0.5, '&:before': { display: 'none' } }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: '#7a8fa6', fontSize: 16 }} />}
        sx={{ minHeight: 40, py: 0, px: 1.5, '& .MuiAccordionSummary-content': { overflow: 'hidden', mr: 1 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', overflow: 'hidden' }}>
          <Chip label={SEV_LABEL[check.severity] || check.severity} size="small"
            sx={{ backgroundColor: `${SEV_COLOR[check.severity]}22`, color: SEV_COLOR[check.severity], fontWeight: 700, fontSize: 10, height: 20, flexShrink: 0 }} />
          <Typography sx={{ color: '#7a8fa6', fontSize: 11, fontFamily: 'monospace', flexShrink: 0, width: 110, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {check.stig_id || check.rule_id?.split('_rule')[0]}
          </Typography>
          <Typography sx={{ color: '#c0cfe0', fontSize: 12, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>
            {check.title}
          </Typography>
          <Chip label={status === 'pass' ? 'NOT A FINDING' : status?.replace(/_/g, ' ').toUpperCase()} size="small"
            sx={{ backgroundColor: `${effectiveColor}22`, color: effectiveColor, fontWeight: 700, fontSize: 10, height: 20, flexShrink: 0 }} />
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ backgroundColor: '#0a0e1a', px: 2, py: 1.5 }}>
        {check.title && (
          <Box sx={{ mb: 1.5 }}>
            <Typography sx={{ color: '#7a8fa6', fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.5 }}>Title</Typography>
            <Typography sx={{ color: '#ffffff', fontSize: 13, fontWeight: 600 }}>{check.title}</Typography>
          </Box>
        )}
        {check.finding && (
          <Box sx={{ mb: 1.5 }}>
            <Typography sx={{ color: '#7a8fa6', fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.5 }}>Finding</Typography>
            <Typography sx={{ color: '#c0cfe0', fontSize: 12, whiteSpace: 'pre-wrap' }}>{check.finding}</Typography>
          </Box>
        )}
        {check.check_content && (
          <Box sx={{ mb: 1.5 }}>
            <Typography sx={{ color: '#7a8fa6', fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.5 }}>Check</Typography>
            <Typography sx={{ color: '#c0cfe0', fontSize: 12, whiteSpace: 'pre-wrap' }}>{check.check_content}</Typography>
          </Box>
        )}
        {check.fix_text && (
          <Box sx={{ mb: 1.5 }}>
            <Typography sx={{ color: '#7a8fa6', fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.5 }}>Fix</Typography>
            <Typography sx={{ color: '#c0cfe0', fontSize: 12, whiteSpace: 'pre-wrap' }}>{check.fix_text}</Typography>
          </Box>
        )}
        <Divider sx={{ borderColor: '#1e3a5f', my: 1.5 }} />
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <Select value={status} onChange={e => setStatus(e.target.value)}
              sx={{ fontSize: 12, color: effectiveColor, backgroundColor: '#111827', '.MuiOutlinedInput-notchedOutline': { borderColor: '#1e3a5f' } }}>
              <MenuItem value="pass">Not a Finding</MenuItem>
              <MenuItem value="fail">Fail</MenuItem>
              <MenuItem value="not_reviewed">Not Reviewed</MenuItem>
              <MenuItem value="not_applicable">Not Applicable</MenuItem>
              <MenuItem value="accepted_risk">Accepted Risk</MenuItem>
            </Select>
          </FormControl>
          <TextField size="small" placeholder="Comment / justification" value={comment}
            onChange={e => setComment(e.target.value)} multiline maxRows={3}
            sx={{ flex: 1, fontSize: 12, '& .MuiInputBase-root': { backgroundColor: '#111827', color: '#c0cfe0', fontSize: 12 }, '& fieldset': { borderColor: '#1e3a5f' } }} />
          <Button variant="contained" size="small" onClick={save} disabled={saving}
            sx={{ backgroundColor: '#00bcd4', color: '#0a0e1a', fontWeight: 700, whiteSpace: 'nowrap' }}>
            {saving ? <CircularProgress size={14} /> : 'Save'}
          </Button>
        </Box>
        {check.reviewed_by && (
          <Typography sx={{ color: '#4a5a70', fontSize: 10, mt: 1 }}>
            Last updated by {check.reviewed_by} · {check.reviewed_at ? new Date(check.reviewed_at).toLocaleString() : ''}
          </Typography>
        )}
      </AccordionDetails>
    </Accordion>
  );
}

function ApplyStigDialog({ assetId, open, onClose, onApplied }) {
  const [library, setLibrary] = useState([]);
  const [search, setSearch]   = useState('');
  const [applying, setApplying] = useState(null);

  useEffect(() => { if (open) getStigLibrary().then(setLibrary); }, [open]);

  const filtered = library.filter(s => s.title.toLowerCase().includes(search.toLowerCase()) || s.filename.toLowerCase().includes(search.toLowerCase()));

  const apply = async (filename) => {
    setApplying(filename);
    await applyStig(assetId, filename);
    setApplying(null);
    onApplied();
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth
      PaperProps={{ sx: { backgroundColor: '#111827', border: '1px solid #1e3a5f' } }}>
      <DialogTitle sx={{ color: '#ffffff', borderBottom: '1px solid #1e3a5f', display: 'flex', justifyContent: 'space-between' }}>
        Apply STIG
        <IconButton onClick={onClose} sx={{ color: '#7a8fa6' }}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        <TextField fullWidth size="small" placeholder="Search STIGs..." value={search} onChange={e => setSearch(e.target.value)}
          sx={{ mb: 2, '& .MuiInputBase-root': { backgroundColor: '#0d1825', color: '#c0cfe0' }, '& fieldset': { borderColor: '#1e3a5f' } }} />
        <Box sx={{ maxHeight: 400, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {filtered.map(s => (
            <Box key={s.filename} sx={{ display: 'flex', alignItems: 'center', gap: 2, p: '8px 12px', backgroundColor: '#0d1825', borderRadius: 1, border: '1px solid #1e3a5f' }}>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ color: '#ffffff', fontSize: 13, fontWeight: 600 }}>{s.title}</Typography>
                <Typography sx={{ color: '#7a8fa6', fontSize: 11 }}>V{s.version}R{s.release} · {s.filename}</Typography>
              </Box>
              <Button size="small" variant="outlined" onClick={() => apply(s.filename)} disabled={applying === s.filename}
                sx={{ borderColor: '#00bcd4', color: '#00bcd4', fontSize: 11, '&:hover': { backgroundColor: '#00bcd411' } }}>
                {applying === s.filename ? <CircularProgress size={14} /> : 'Apply'}
              </Button>
            </Box>
          ))}
        </Box>
      </DialogContent>
    </Dialog>
  );
}

const TABS = ['all', 'high', 'medium', 'low', 'not_reviewed'];
const TAB_LABEL = { all: 'All', high: 'CAT I', medium: 'CAT II', low: 'CAT III', not_reviewed: 'Not Reviewed' };

export default function StigDetailDrawer({ asset, open, onClose }) {
  const [checks, setChecks]         = useState([]);
  const [loading, setLoading]       = useState(false);
  const [tab, setTab]               = useState(0);
  const [applyOpen, setApplyOpen]   = useState(false);

  const load = useCallback(() => {
    if (!asset) return;
    setLoading(true);
    getAssetChecks(asset.id).then(data => { setChecks(data); setLoading(false); });
  }, [asset]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const filtered = tab === 0
    ? checks
    : TABS[tab] === 'not_reviewed'
      ? checks.filter(c => c.status === 'not_reviewed')
      : checks.filter(c => c.severity === TABS[tab]);

  const handleCklUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !asset) return;
    await uploadCkl(asset.id, file);
    load();
  };

  const counts = {
    high:         checks.filter(c => c.severity === 'high').length,
    medium:       checks.filter(c => c.severity === 'medium').length,
    low:          checks.filter(c => c.severity === 'low').length,
    not_reviewed: checks.filter(c => c.status === 'not_reviewed').length,
  };

  return (
    <>
      <Drawer anchor="right" open={open} onClose={onClose}
        PaperProps={{ sx: { width: 720, backgroundColor: '#0a0e1a', borderLeft: '1px solid #1e3a5f', display: 'flex', flexDirection: 'column', overflow: 'hidden' } }}>

        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2, borderBottom: '1px solid #1e3a5f', backgroundColor: '#111827' }}>
          <Box>
            <Typography sx={{ color: '#ffffff', fontWeight: 700, fontSize: 16, fontFamily: 'monospace' }}>{asset?.hostname}</Typography>
            <Typography sx={{ color: '#7a8fa6', fontSize: 12 }}>{asset?.os} · STIG Checks</Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title="Upload .ckl checklist">
              <IconButton component="label" sx={{ color: '#7a8fa6', '&:hover': { color: '#00bcd4' } }}>
                <UploadFileIcon />
                <input type="file" accept=".ckl" hidden onChange={handleCklUpload} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Apply STIG from library">
              <IconButton onClick={() => setApplyOpen(true)} sx={{ color: '#7a8fa6', '&:hover': { color: '#00bcd4' } }}>
                <AddCircleOutlineIcon />
              </IconButton>
            </Tooltip>
            <IconButton onClick={onClose} sx={{ color: '#7a8fa6' }}><CloseIcon /></IconButton>
          </Box>
        </Box>

        {/* Stats */}
        <Box sx={{ display: 'flex', gap: 0, borderBottom: '1px solid #1e3a5f' }}>
          {[
            { label: 'CAT I',   count: counts.high,          color: '#f44336' },
            { label: 'CAT II',  count: counts.medium,        color: '#ff9800' },
            { label: 'CAT III', count: counts.low,           color: '#ffeb3b' },
            { label: 'Open',    count: counts.not_reviewed,  color: '#7a8fa6' },
            { label: 'Total',   count: checks.length,        color: '#ffffff' },
          ].map(s => (
            <Box key={s.label} sx={{ flex: 1, textAlign: 'center', py: 1.5, borderRight: '1px solid #1e3a5f' }}>
              <Typography sx={{ color: s.color, fontWeight: 700, fontSize: 20 }}>{s.count}</Typography>
              <Typography sx={{ color: '#7a8fa6', fontSize: 10, textTransform: 'uppercase' }}>{s.label}</Typography>
            </Box>
          ))}
        </Box>

        {/* Tabs */}
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: '1px solid #1e3a5f', minHeight: 36,
          '& .MuiTab-root': { color: '#7a8fa6', fontSize: 11, minHeight: 36, textTransform: 'uppercase' },
          '& .Mui-selected': { color: '#00bcd4' },
          '& .MuiTabs-indicator': { backgroundColor: '#00bcd4' } }}>
          {TABS.map((t, i) => <Tab key={t} label={TAB_LABEL[t]} />)}
        </Tabs>

        {/* Check list */}
        <Box sx={{ flex: 1, overflowY: 'auto', p: 1.5 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', pt: 4 }}><CircularProgress sx={{ color: '#00bcd4' }} /></Box>
          ) : filtered.length === 0 ? (
            <Typography sx={{ color: '#4a5a70', textAlign: 'center', mt: 4 }}>No checks found</Typography>
          ) : (
            filtered.map(c => <CheckRow key={c.id} check={c} onUpdate={load} />)
          )}
        </Box>
      </Drawer>

      <ApplyStigDialog assetId={asset?.id} open={applyOpen} onClose={() => setApplyOpen(false)} onApplied={load} />
    </>
  );
}
